import { useNavigate } from "@tanstack/react-router";
import { useDrawer } from "hooks/useDrawer";
import { usePermission } from "hooks/usePermissions";
import { useUser } from "hooks/useUser";
import { useState, useRef, type MouseEvent } from "react";
import { PermissionEnum } from "types/enums.ts";
import { DrawerId } from "types/models";

const isHovered = (rowIndex: number, hoveredRow?: number): boolean => rowIndex === hoveredRow;

export interface RowActionConfig {
    type: "link" | "drawer";
    permission?: PermissionEnum;
    condition?: () => boolean;
    url?: string;
    drawerId?: DrawerId;
    drawerParam?: string;
}

export interface SmartRowActionConfig {
    entityName: string; // e.g., "offer", "affiliate", "advertiser"
    editUrl?: string; // URL pattern for edit page, e.g., "edit/:id/general"
    detailUrl?: string; // URL pattern for detail page, e.g., "detail/:id"
    editPermission?: PermissionEnum; // Permission for edit action
    detailPermission?: PermissionEnum; // Permission for detail/view action
    detailDrawerId?: DrawerId; // Drawer for detail view
    detailDrawerParam?: string; // Parameter for detail drawer
}

interface UseRowHandlersProps {
    isRowClickable?: boolean;
    actions?: RowActionConfig[];
    smartActions?: SmartRowActionConfig;
    excludeColumns?: string[]; // Column keys to exclude from row click behavior
}

const createSmartActions = (
    smartConfig: SmartRowActionConfig,
    permissionsUtils: ReturnType<typeof usePermission>
): RowActionConfig[] => {
    const actions: RowActionConfig[] = [];

    // Priority 1: Edit action (if user has edit permission)
    if (smartConfig.editPermission && smartConfig.editUrl) {
        actions.push({
            type: "link",
            url: smartConfig.editUrl,
            permission: smartConfig.editPermission,
        });
    }

    // Priority 2: Detail action (if no edit URL is defined)
    if (smartConfig.detailPermission && !smartConfig.editUrl) {
        if (smartConfig.detailDrawerId) {
            actions.push({
                type: "drawer",
                drawerId: smartConfig.detailDrawerId,
                drawerParam: smartConfig.detailDrawerParam ?? smartConfig.detailDrawerId,
                permission: smartConfig.detailPermission,
            });
        } else if (smartConfig.detailUrl) {
            actions.push({
                type: "link",
                url: smartConfig.detailUrl,
                permission: smartConfig.detailPermission,
            });
        }
    }

    // Priority 3: Detail view (fallback for users without edit permission when edit URL exists)
    if (smartConfig.detailPermission && smartConfig.editUrl && smartConfig.editPermission) {
        if (smartConfig.detailDrawerId) {
            actions.push({
                type: "drawer",
                drawerId: smartConfig.detailDrawerId,
                drawerParam: smartConfig.detailDrawerParam ?? smartConfig.detailDrawerId,
                permission: smartConfig.detailPermission,
                condition: () => !permissionsUtils.hasPermission(smartConfig.editPermission!),
            });
        } else if (smartConfig.detailUrl) {
            actions.push({
                type: "link",
                url: smartConfig.detailUrl,
                permission: smartConfig.detailPermission,
                condition: () => !permissionsUtils.hasPermission(smartConfig.editPermission!),
            });
        }
    }

    return actions;
};

export const useRowHandlers = <T extends { id?: string | number | null }>({
    isRowClickable = true,
    actions = [],
    smartActions,
    excludeColumns = [],
}: UseRowHandlersProps) => {
    const drawerUtils = useDrawer();
    const permissionsUtils = usePermission();
    const userUtils = useUser();
    const [hoveredRow, setHoveredRow] = useState<number | undefined>();
    const navigate = useNavigate();

    // Generate actions from smart config if provided
    const finalActions = smartActions ? [...createSmartActions(smartActions, permissionsUtils), ...actions] : actions;

    const getActionUrl = (record: T): string | null => {
        if (!isRowClickable || !record.id) {
            return null;
        }

        for (const action of finalActions) {
            const hasPermission = action.permission ? permissionsUtils.hasPermission(action.permission) : true;
            const conditionResult = !action.condition || action.condition();

            if (hasPermission && conditionResult) {
                if (action.type === "link" && action.url) {
                    const tenantSlug = userUtils.getActiveTenant()?.slug_name;
                    let url = action.url.replace(":id", record.id.toString());

                    if (tenantSlug && url.includes("$tenantSlug")) {
                        url = url.replace("$tenantSlug", tenantSlug);
                    }

                    return url;
                } else if (action.type === "drawer" && action.drawerId) {
                    const drawerParam = action.drawerParam ?? action.drawerId;
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.set("drawerIds", action.drawerId);
                    currentUrl.searchParams.set(drawerParam, record.id.toString());
                    const drawerUrl = currentUrl.toString();
                    return drawerUrl;
                }
            }
        }
        return null;
    };

    const handleRowClick = (record: T, event: MouseEvent) => {
        if (!isRowClickable) {
            return;
        }

        const clickedElement = event.target as HTMLElement;

        if (clickedElement.closest(".ace_editor") || clickedElement.closest(".ace-github")) {
            return;
        }

        const tableCell = clickedElement.closest("td");
        if (tableCell) {
            const columnKey = tableCell.getAttribute("data-column-key");
            if (columnKey && excludeColumns.includes(columnKey)) {
                return;
            }

            if (tableCell.querySelector(".ace_editor") && excludeColumns.includes("response")) {
                return;
            }
        }

        // Middle click or Ctrl/Cmd + click - open in new tab
        if (event.button === 1 || event.ctrlKey || event.metaKey) {
            // Find the first valid action for this record
            for (const action of finalActions) {
                const hasPermission = action.permission ? permissionsUtils.hasPermission(action.permission) : true;
                const conditionResult = !action.condition || action.condition();

                if (hasPermission && conditionResult && record.id) {
                    if (action.type === "link" && action.url) {
                        const tenantSlug = userUtils.getActiveTenant()?.slug_name;
                        let url = action.url.replace(":id", record.id.toString());

                        if (tenantSlug && url.includes("$tenantSlug")) {
                            url = url.replace("$tenantSlug", tenantSlug);
                        }

                        window.open(url, "_blank");
                        event.preventDefault();
                        return;
                    } else if (action.type === "drawer" && action.drawerId) {
                        // For drawer actions, middle click should open drawer URL in new tab
                        const drawerParam = action.drawerParam ?? action.drawerId;
                        const currentUrl = new URL(window.location.href);
                        currentUrl.searchParams.set("drawerIds", action.drawerId);
                        currentUrl.searchParams.set(drawerParam, record.id.toString());
                        const drawerUrl = currentUrl.toString();

                        window.open(drawerUrl, "_blank");
                        event.preventDefault();
                        return;
                    }
                }
            }
        }

        // Normal left click
        if (event.button === 0) {
            for (const action of finalActions) {
                const hasPermission = action.permission ? permissionsUtils.hasPermission(action.permission) : true;
                const conditionResult = !action.condition || action.condition();

                if (hasPermission && conditionResult && record.id) {
                    if (action.type === "link" && action.url) {
                        const tenantSlug = userUtils.getActiveTenant()?.slug_name;
                        let navigateUrl = action.url.replace(":id", record.id.toString());

                        // Replace $tenantSlug placeholder with actual tenant slug
                        if (tenantSlug && navigateUrl.includes("$tenantSlug")) {
                            navigateUrl = navigateUrl.replace("$tenantSlug", tenantSlug);
                        }

                        const routeParams = {
                            to: navigateUrl,
                        };
                        void navigate(routeParams);
                        return;
                    } else if (action.type === "drawer" && action.drawerId) {
                        const drawerParams = {
                            [action.drawerParam ?? action.drawerId]: record.id,
                        };
                        drawerUtils.show(action.drawerId, drawerParams);
                        return;
                    }
                }
            }
        }
    };

    const handleContextMenu = (record: T, event: MouseEvent) => {
        if (!isRowClickable || !record.id) {
            return;
        }

        // Find the first valid action for this record
        for (const action of finalActions) {
            const hasPermission = action.permission ? permissionsUtils.hasPermission(action.permission) : true;
            const conditionResult = !action.condition || action.condition();

            if (hasPermission && conditionResult) {
                if (action.type === "link" && action.url) {
                    const tenantSlug = userUtils.getActiveTenant()?.slug_name;
                    let url = action.url.replace(":id", record.id.toString());

                    if (tenantSlug && url.includes("$tenantSlug")) {
                        url = url.replace("$tenantSlug", tenantSlug);
                    }

                    // Create a temporary link element for context menu
                    event.preventDefault();
                    const link = document.createElement("a");
                    link.href = url;
                    link.style.display = "none";
                    document.body.appendChild(link);

                    // Trigger context menu on the link
                    const contextMenuEvent = new MouseEvent("contextmenu", {
                        bubbles: true,
                        cancelable: true,
                        clientX: event.clientX,
                        clientY: event.clientY,
                    });
                    link.dispatchEvent(contextMenuEvent);

                    setTimeout(() => {
                        document.body.removeChild(link);
                    }, 100);
                    return;
                } else if (action.type === "drawer") {
                    // For drawer actions, don't prevent default context menu
                    // This allows normal browser context menu to appear
                    return;
                }
            }
        }
    };

    const handleMouseEnter = (rowIndex: number) => setHoveredRow(rowIndex);
    const handleMouseLeave = () => setHoveredRow(undefined);

    // Track middle mouse button timing with useRef to persist across re-renders
    const middleClickDownTimeRef = useRef<number>(0);
    const middleClickMovedRef = useRef<boolean>(false);

    const getRowHandlers = (record: T, rowIndex?: number) => ({
        onClick: (event: MouseEvent) => handleRowClick(record, event),
        onMouseDown: (event: MouseEvent) => {
            // Track when middle button is pressed
            if (event.button === 1) {
                middleClickDownTimeRef.current = Date.now();
                middleClickMovedRef.current = false;
                
                // Add mousemove listener to detect if user is trying to scroll
                const handleMouseMove = () => {
                    middleClickMovedRef.current = true;
                    document.removeEventListener('mousemove', handleMouseMove);
                };
                document.addEventListener('mousemove', handleMouseMove, { once: true });
                
                // Clean up listener after a short time
                setTimeout(() => {
                    document.removeEventListener('mousemove', handleMouseMove);
                }, 500);
            }
        },
        onMouseUp: (event: MouseEvent) => {
            // Handle middle click release
            if (event.button === 1) {
                const clickDuration = Date.now() - middleClickDownTimeRef.current;
                
                // If it was a quick click (< 500ms) and mouse didn't move, open in new tab
                if (clickDuration < 500 && !middleClickMovedRef.current) {
                    event.preventDefault();
                    handleRowClick(record, event);
                }
                // Otherwise, let browser handle scrolling naturally
                
                middleClickDownTimeRef.current = 0;
                middleClickMovedRef.current = false;
            }
        },
        onContextMenu: (event: MouseEvent) => handleContextMenu(record, event),
        onMouseEnter: () => handleMouseEnter(rowIndex ?? 0),
        onMouseLeave: handleMouseLeave,
        style: { cursor: isRowClickable ? "pointer" : "default" },
        "data-url": getActionUrl(record) || undefined,
    });

    const getRowClassName = (rowIndex: number): string => {
        const classes: string[] = [];
        if (isHovered(rowIndex, hoveredRow) && isRowClickable) {
            classes.push("hovered-row");
        }
        if (isRowClickable) {
            classes.push("clickable-row");
        }
        return classes.join(" ");
    };

    return { getRowHandlers, getRowClassName };
};

// Convenience hook for smart actions
export const useSmartRowHandlers = <T extends { id?: string | number | null }>(
    smartConfig: SmartRowActionConfig,
    options: { isRowClickable?: boolean; additionalActions?: RowActionConfig[]; excludeColumns?: string[] } = {}
) => {
    return useRowHandlers<T>({
        isRowClickable: options.isRowClickable ?? true,
        smartActions: smartConfig,
        actions: options.additionalActions || [],
        excludeColumns: options.excludeColumns || [],
    });
};
